import { useCallback, useEffect } from 'react'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import { isBrowserRuntime, navigateToHighlightRoute } from '@renderer/browser/browser-utils'
import { usePaywallState } from '@renderer/chat/store/paywalls'
import { useUnifiedBilling } from '@renderer/chat/store/global-billing'
import WindowIpcRenderer from '@renderer/ipc/WindowIpc.renderer'
import { usePlanAllowances } from '@renderer/chat/hooks/use-plan-allowances'
import type { TopUpKind } from '@renderer/components/pricing/top-up-kinds'
import { subscriptionPlansRoute, subscriptionRoute, subscriptionTopUpRoute } from '@shared/settings/settings-deeplink'
import { useClearSidepanelPaywallRequest, useSidepanelPaywallRequest } from '../sidepanel-paywall-request'
import { resolveTopUpStrips } from '../sidepanel-paywall-strips'
import { PlanBanner } from './plan-banner'
import {
  nextPlanBanner,
  planBannerCopy,
  resolvePlanBanners,
  type PlanBanner as PlanBannerModel,
  type PlanBannerInputs,
} from './plan-banner-rules'
import { useBriefLimitPeriodEnd, useDismissedPlanBanners, useDismissPlanBanner } from './plan-banner-state'

const SURFACE = 'sidepanel home'

/**
 * The revamp's sidepanel banners: one at a time, most urgent first, and
 * closing one brings up the next. Every action opens Settings → Subscription,
 * on the plan comparison when it sells an upgrade.
 */
export function SidepanelPlanBanners() {
  const inputs = usePlanBannerInputs()
  const clearRequest = useClearSidepanelPaywallRequest()
  const dismissed = useDismissedPlanBanners()
  const dismiss = useDismissPlanBanner()

  // Same request expiry as the pre-revamp strips (HIG-6471).
  const { staleRequest } = resolveTopUpStrips(inputs)
  useEffect(() => {
    if (staleRequest) {
      clearRequest(staleRequest)
    }
  }, [staleRequest, clearRequest])

  const banner = nextPlanBanner(resolvePlanBanners(inputs), dismissed)
  if (!banner) return null
  return <ShownPlanBanner key={banner.dismissKey} banner={banner} onDismiss={() => dismiss(banner.dismissKey)} />
}

/** Everything the banner rules read, for the banners and for the surfaces that lock on the same limits. */
export function usePlanBannerInputs(): PlanBannerInputs {
  const paywall = usePaywallState()
  const { subscription, billingProfile, lastUpdated } = useUnifiedBilling()
  const request = useSidepanelPaywallRequest()
  const briefLimitPeriodEnd = useBriefLimitPeriodEnd()
  const allowances = usePlanAllowances().data
  return {
    paywall,
    subscription,
    previousPlan: billingProfile?.previousPlan,
    request,
    billingUpdatedAt: lastUpdated,
    briefLimitPeriodEnd,
    briefsLeft: allowances?.meetingBriefs?.remaining ?? null,
    followUps: allowances?.actionUnlocks ?? null,
  }
}

function ShownPlanBanner({ banner, onDismiss }: { banner: PlanBannerModel; onDismiss: () => void }) {
  const paywall = usePaywallState()
  const { subscription } = useUnifiedBilling()
  const { kind, action, ctaPlan } = banner

  useEffect(() => {
    AmplitudeRenderer.trackEvent('Paywall', {
      action: 'shown',
      feature: kind,
      reason: action,
      plan: ctaPlan,
      from: SURFACE,
    })
  }, [kind, action, ctaPlan])

  const onAction = useCallback(
    (topUp = banner.topUp) => {
      AmplitudeRenderer.trackEvent('Paywall', {
        action: 'clicked',
        feature: kind,
        reason: action,
        plan: ctaPlan,
        from: SURFACE,
      })
      openSubscriptionSettings(planBannerRoute(action, topUp))
    },
    [banner.topUp, kind, action, ctaPlan],
  )

  return (
    <PlanBanner
      kind={kind}
      copy={planBannerCopy(banner, { paywall, subscription })}
      ctaPlan={ctaPlan}
      onAction={() => onAction()}
      onSecondaryAction={() => onAction(banner.secondaryTopUp)}
      onDismiss={onDismiss}
    />
  )
}

/** Where a banner's action lands: the plans for an upgrade, the purchase for a top-up, Subscription otherwise. */
export function planBannerRoute(action: PlanBannerModel['action'], topUp?: TopUpKind): string {
  if (action === 'upgrade') return subscriptionPlansRoute()
  return topUp ? subscriptionTopUpRoute(topUp) : subscriptionRoute()
}

export function openSubscriptionSettings(route: string) {
  if (isBrowserRuntime()) {
    navigateToHighlightRoute(route)
    return
  }
  WindowIpcRenderer.openSettingsWindow(route).catch((error) => {
    console.error('Failed to open subscription settings:', error)
  })
}
